<script>
import pagination from '@/mixins/pagination';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import {mapActions, mapMutations, mapState, mapGetters} from 'vuex';
import ElDialog from '../Dialog';
import TrekButton from '@/components/TrekButton';
import {PdlCollapse, PdlCollapseItem} from '@pedal/pdl-collapse';
import {PdlTabs, PdlTabPane} from '@pedal/pdl-tabs';
import CartProductGrid from '@/components/cart/CartProductGrid';
import SavedListsPanel from '@/components/saved-lists/SavedListsPanel';
import AppleLabelGrid from '@/components/checkout/AppleLabelGrid';
import Pagination from '@/components/Pagination.vue';
import FormGroup from '@/components/FormGroup';
import {PdlCallout} from '@pedal/pdl-callout';
import {PdlSwatch} from '@pedal/pdl-swatch';
import swatchMixin from '@/mixins/swatches.js';
import {formatAppleData} from '@/utils/product-info';

export default {
  components: {
    TrekButton,
    Pagination,
    ElDialog,
    PdlCollapse,
    PdlCollapseItem,
    PdlTabs,
    PdlTabPane,
    CartProductGrid,
    SavedListsPanel,
    AppleLabelGrid,
    FormGroup,
    PdlCallout,
    PdlSwatch,
  },
  mixins: [pagination, swatchMixin],

  props: {
    cartId: {
      type: String,
      default: '',
    },
    listData: {
      type: Array,
      default: () => [],
    },
    partTypeKey: {
      type: String,
      default: '',
    },
    itemIdKey: {
      type: String,
      default: '',
    },
    cartSummary: {
      type: Object,
      default: () => {},
    },
    size: {
      type: Number,
      required: false,
      default: ACC.cartItemsPageSize || 15,
    },
  },

  data() {
    return {
      isDialogVisible: false,
      isAllChecked: false,
    };
  },

  computed: {
    isDisabled() {
      return (
        (this.partTypeKey === 'bikes' && this.selectedProductsBikeCount === 0) ||
        (this.partTypeKey === 'projectOneBikes' && this.selectedProductsBikeCount === 0) ||
        (this.partTypeKey !== 'bikes' &&
          this.partTypeKey !== 'projectOneBikes' &&
          this.selectedProductsAccessoriesCount === 0) ||
        false
      );
    },
    collapseRef() {
      return `collapse-${this.partTypeKey}`;
    },
    checkboxId() {
      return `cart-checkbox-check-all-${this.partTypeKey}`;
    },
    checkboxValue() {
      return `CheckAll${this.partTypeKey}`;
    },
    isCustomWaterBottle() {
      return this.partTypeKey === 'customWaterBottles';
    },
    isProjectOne() {
      return this.partTypeKey === 'projectOneBikes';
    },

    ...mapState('backend', ['cartToggle', 'appleLabelAccess']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
    ...mapGetters('cart', ['selectedProductsBikeCount', 'selectedProductsAccessoriesCount', 'selectedProducts']),
  },
  watch: {
    selectedProducts() {
      this.isAllChecked = this.getEntries().every(
        (entry) => !!this.selectedProducts.find((product) => entry.cartEntryPk === product.cartEntryPk)
      );
    },
  },
  methods: {
    resetDeleteData() {
      this.isDialogVisible = false;
    },
    confirmDelete() {
      this.deleteSelectedProducts(this.partTypeKey);
      this.isDialogVisible = false;
    },
    getAppleLabelData(item) {
      if (!this.displayAppleLabels) return [];

      return formatAppleData(item.entries);
    },

    updateSavedList() {
      if (this.partTypeKey === 'bikes') {
        this.setEntries(this.selectedProducts.filter((product) => product.productType === 'bike'));
      } else {
        this.setEntries(this.selectedProducts.filter((product) => product.productType !== 'bike'));
      }
    },

    getProductQuantity(product) {
      return reduce(product.warehouses, (result, warehouse) => result + parseInt(warehouse.orderedQty), 0);
    },

    displayAppleLabels(partTypeKey) {
      return this.appleLabelAccess[partTypeKey] && !isEmpty(this.listData[0].entries[0].appleLabels);
    },

    getPartKeyQAID(name) {
      return `cart.${this.partTypeKey}.tab.${name}`;
    },

    getGroupDisplayImage(product) {
      let imageSuffix =
        '?wid=160&amp;hei=120&amp;fmt=png8-alpha&amp;qlt=80,1&amp;op_usm=0,0,0,0&amp;iccEmbed=0&amp;cache=on,on';

      let image = get(product, 'entries[0].variantImage');

      if (this.isProjectOne) {
        return image ? ACC.scene7ProductImageUrl + image : null;
      } else {
        return image ? ACC.scene7ProductImageUrl + image + imageSuffix : null;
      }
    },
    getGroupDisplayColor(product) {
      return get(product, 'entries[0].variantValueCategories[0].Color.code');
    },
    getColorProps(product) {
      const code = this.getGroupDisplayColor(product);

      return this.processColorCode(code);
    },

    getGroupDisplayColorName(product) {
      return get(product, 'entries[0].variantValueCategories[0].Color.name');
    },

    getGroupingVariant(product) {
      const variantValueCategory = get(product, 'entries[0].variantValueCategories[0]');
      return variantValueCategory ? variantValueCategory[product.groupBy] : null;
    },

    getCollapseItemName(partTypeKey, itemIdKey, index) {
      return `${partTypeKey}-${itemIdKey}-${index}`;
    },

    getProductGridKey(item, index) {
      return `grid-${this.partTypeKey}-${item.entries[0].cartEntryPk}-${index}`;
    },

    getEntries() {
      const entries = [];
      this.listData.forEach((product) => {
        product.entries.forEach((entry) => {
          entries.push(entry);
        });
      });
      return entries;
    },

    updatePage(page) {
      if (page !== this.pageNumber) {
        this.pageNumber = page;
      }

      this.$nextTick(() => {
        if (this.$refs[this.collapseRef] !== null) {
          this.$refs[this.collapseRef][0].setAllActive();
        }
      });
    },

    checkAllItems() {
      this.isAllChecked = !this.isAllChecked;
      if (this.isAllChecked) {
        this.getEntries().forEach((entry) => {
          this.addSelectedProduct({
            sku: entry.sku,
            qty: this.getProductQuantity(entry),
            productType: entry.productType,
            lineNote: entry.lineNote,
            productRecipeID: entry.productRecipeID,
            cartEntryPk: entry.cartEntryPk,
          });
        });
      } else {
        this.clearSelectedProducts(this.partTypeKey);
      }
    },

    ...mapMutations('savedLists', ['setEntries']),

    ...mapActions('cart', [
      'removeProducts',
      'undoRemove',
      'clearSelectedProducts',
      'updateCart',
      'deleteSelectedProducts',
      'addSelectedProduct',
    ]),
  },
};
</script>
